import { inject, Injectable, Injector } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { createProviderToken, provide } from '@core/common-legacy';
import { HttpApiService } from '@core/http-legacy';
import { HttpApiRequestOptions, HttpRequestData } from '@core/http-types';
import { TellentAdminRequestData } from '@core/tellent-http-legacy';
import type { TellentMemberBrief } from '@tellent-ask-hr/core-types';
import type { TellentNote } from '@tellent-ask-hr/notes-types';

@Injectable()
export class TellentNotesRequestData extends TellentAdminRequestData {
  public buildUrl(path: string, requestOptions: HttpApiRequestOptions): string {
    return `${this.config.tellentApiUrl}/notes/org/${this.orgGuid}${path}`;
  }
}

export const { inject: injectNotesHttpApi } = createProviderToken('TellentAskHttp')(false, {
  providedIn: 'root',
  factory: () =>
    Injector.create({
      providers: [
        provide(HttpApiService, { useClass: HttpApiService }),
        provide(HttpRequestData, { useClass: TellentNotesRequestData }),
      ],
      parent: inject(Injector),
    }).get(HttpApiService),
});

@Injectable({ providedIn: 'root' })
export class TellentNotesHttpClient {
  private readonly httpApi = injectNotesHttpApi();

  public deleteNote(id: number): Observable<void> {
    return this.httpApi.delete(`/notes/${id}`).pipe(map(() => undefined));
  }

  public updateNote(id: number, note: { bodyJson: object }): Observable<TellentNote> {
    return this.httpApi.patch(`/notes/${id}`, null, note).pipe(map(res => res.data));
  }

  public loadNotes(
    resourceType: TellentNote['resourceType'],
    resourceId: number,
  ): Observable<TellentNote[]> {
    return this.httpApi.get(`/${resourceType}/${resourceId}/notes`).pipe(map(res => res.data));
  }

  public createNote(
    resourceType: TellentNote['resourceType'],
    resourceId: number,
    note: { bodyJson: object },
  ): Observable<TellentNote> {
    return this.httpApi
      .post(`/${resourceType}/${resourceId}/notes`, null, note)
      .pipe(map(res => res.data));
  }

  public fetchMentionables(
    resourceType: TellentNote['resourceType'],
    resourceId: number,
  ): Observable<TellentMemberBrief[]> {
    return this.httpApi
      .get(`/${resourceType}/${resourceId}/mentionables`)
      .pipe(map(res => res.data.members));
  }
}
